import { styled } from '@linaria/react';
import { Handle, NodeProps, NodeToolbar, Position } from '@xyflow/react';
import { memo, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stages } from '../../../api/process-client/process-client.type';
import { useProcessStore } from '../../../store/process-store/process-store.hooks';
import { Button } from '../../../ui-library/button/button';
import { ProcessContext } from '../context/process.context';
import { Text } from '../process.styled';
import { getStageI18nKey, getStageOverrideLabel } from '../processv2.helpers';
import type { StageNodeData } from '../reactflow.type';
import { getBackground, getBorder } from './stage-node.helpers';

const StageNode = memo(({ data }: NodeProps) => {
  const { t } = useTranslation();
  const [showToolbar, setShowToolbar] = useState(false);
  const { setEventScope: setEventScopeStore, setRightEventScope: setRightEventScopeStore } = useProcessStore();
  const { setEventScope, setRightEventScope } = useContext(ProcessContext);
  const nodeData = data as StageNodeData;

  // update both context and store to be backwards compatible with V1
  const navigateToEventGraph = () => {
    if (nodeData.isRightGraph) {
      const eventScope = {
        showEventGraph: true,
        statuses: nodeData.customerStages || [],
        statusGroup: nodeData.title,
        selectedEdgeId: null,
      };
      setRightEventScopeStore(eventScope);
      setRightEventScope(eventScope);
    } else {
      const eventScope = {
        showEventGraph: true,
        statuses: nodeData.customerStages || [],
        statusGroup: nodeData.title,
        selectedEdgeId: null,
      };
      setEventScopeStore(eventScope);
      setEventScope(eventScope);
    }
  };

  return (
    <>
      <NodeToolbar isVisible={showToolbar} position={nodeData.toolbarPosition || Position.Top}>
        <Button size="xs" variant="secondary" onClick={navigateToEventGraph}>
          {`${t('common.work_unit')} ${t('process.event_graph.event')} Graph`}
        </Button>
      </NodeToolbar>
      <StageNodeComponent data={nodeData} onNodeClick={(showToolbar) => setShowToolbar(showToolbar)} />
      <Handle type="target" position={Position.Top} id="top" />
      <Handle type="source" position={Position.Bottom} id="bottom" />
      <Handle type="source" position={Position.Right} id="right" />
      <Handle type="target" position={Position.Right} id="right-target" />
      <Handle type="source" position={Position.Left} id="left" />
      <Handle type="target" position={Position.Left} id="left-target" />
    </>
  );
});

const StageNodeComponent = ({
  data,
  onNodeClick,
}: {
  data: StageNodeData;
  onNodeClick: (showToolbar: boolean) => void;
}) => {
  const { t } = useTranslation();
  const isRightGraph = data.isRightGraph;

  const {
    stages: leftStagesStore,
    setStages: setLeftStagesStore,
    rightStages: rightStagesStore,
    setRightStages: setRightStagesStore,
  } = useProcessStore();

  const stages = isRightGraph ? rightStagesStore : leftStagesStore;
  const setStages = isRightGraph ? setRightStagesStore : setLeftStagesStore;

  const onClick = useCallback(
    (stage: Stages) => {
      if (stages.includes(stage)) {
        setStages(stages.filter((s) => s !== stage));
        onNodeClick(false);
      } else {
        setStages([...stages, stage]);
        onNodeClick(true);
      }
    },
    [stages, setStages, onNodeClick],
  );

  const isActive = stages.includes(data.stage);
  const isGrayed = data.stage === Stages.Blocked || (data.stage as string) === 'Waiting';

  // Translate the stage title reactively (supports language changes).
  // Per-workflow `bucket_label_overrides` win over i18n when the
  // graph builder supplied them on `data.resolvedLabels`.
  const overrideLabel = getStageOverrideLabel(data.title, data.resolvedLabels);
  const i18nKey = getStageI18nKey(data.title);
  const translatedTitle: string = overrideLabel ?? (i18nKey != null ? t(i18nKey) : data.title);

  return (
    <NodeContainer active={isActive} grayed={isGrayed} onClick={() => onClick(data.stage)}>
      <Text fontSize={12} active={isActive} grayed={isGrayed} style={{ wordBreak: 'break-word' }}>
        {translatedTitle}
      </Text>
      {data.customerStages?.map((stage: string, index: number) => (
        <Text
          key={stage}
          fontSize={14}
          fontWeight={700}
          active={isActive}
          grayed={isGrayed}
          style={{ wordWrap: 'break-word' }}
        >
          {`${data.customerStageLabels?.[stage] ?? stage}${
            index === (data.customerStages?.length || 0) - 1 ? '' : ','
          }`}
        </Text>
      ))}
    </NodeContainer>
  );
};

export { StageNode };

const NodeContainer = styled.div<{ active: boolean; grayed: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 160px;
  border-radius: 6px;
  border: 1px solid ${(props: { active: boolean; grayed: boolean }) => getBorder(props.active, props.grayed)};
  background: ${(props: { active: boolean; grayed: boolean }) => getBackground(props.active, props.grayed)};
  padding: 12px 6px;
  cursor: pointer;
`;
